import { useState } from 'react';
import { useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import { ChartNoAxesColumnIncreasing, ChevronDown, ChevronUp, KeyRound } from 'lucide-react-native';
import { colors } from '@/lib/theme';
import { trpc } from '@/lib/trpc';
import { useAppTheme } from '@/theme';
import { Box, PressableBox, Text } from './ui/primitives';

type RankStat = { value: number | null; rank: number | null };
type PowerRatingStat = { value: number; rank: number };
type TeamPowerRatings = {
  overall: PowerRatingStat;
  offense: PowerRatingStat;
  defense: PowerRatingStat;
  gamesPlayed: number;
  isPreseasonBaseline: boolean;
};

export type MatchupTeam = {
  abbreviation: string;
  name: string;
  record: { overall: string; home: string; road: string; streak: string | null } | null;
  offense: Record<'totalYards' | 'passingYards' | 'rushingYards' | 'points', RankStat>;
  defense: Record<'totalYards' | 'passingYards' | 'rushingYards' | 'points', RankStat>;
  lastFive?: {
    record: RecentRecord | null;
    ats: RecentRecord | null;
  };
  injuries: {
    playerName: string;
    position: string | null;
    status: string | null;
    description: string | null;
  }[];
  standings?: {
    conferenceRecord: string | null;
    divisionRecord: string | null;
    conferenceRank: number | null;
    divisionRank: number | null;
    pointsFor: number | null;
    pointsAgainst: number | null;
    pointDifferential: number | null;
  } | null;
  seasonStats?: {
    gamesPlayed: number | null;
    pointsPerGame: number | null;
    pointsAllowedPerGame: number | null;
    totalYardsPerGame: number | null;
    totalYardsAllowedPerGame: number | null;
    passingYardsPerGame: number | null;
    passingYardsAllowedPerGame: number | null;
    rushingYardsPerGame: number | null;
    rushingYardsAllowedPerGame: number | null;
    yardsPerPlay: number | null;
    turnoverDifferential: number | null;
    thirdDownConversionPct: number | null;
    fourthDownConversionPct: number | null;
    redZoneScorePct: number | null;
    penaltyYardsPerGame: number | null;
    possessionTimePerGameSeconds: number | null;
  } | null;
  recentStats?: {
    gamesPlayed: number;
    pointsPerGame: number | null;
    pointsAllowedPerGame: number | null;
    totalYardsPerGame: number | null;
    totalYardsAllowedPerGame: number | null;
    yardsPerPlay: number | null;
    turnoverDifferential: number | null;
    thirdDownConversionPct: number | null;
    redZoneScorePct: number | null;
  } | null;
};

export type MatchupAnalysis = {
  awayTeam: MatchupTeam;
  homeTeam: MatchupTeam;
  statsAvailable: boolean;
  injuriesAvailable: boolean;
  rankingsSource: 'current-season' | 'previous-season' | null;
  dataSource?: 'api-nfl' | 'balldontlie';
  powerRatings?: {
    awayTeam: TeamPowerRatings | null;
    homeTeam: TeamPowerRatings | null;
    sourceLabel: 'Current season' | 'Preseason baseline' | 'Unavailable';
  };
  markets?: MatchupMarket[];
  story?: {
    title: string;
    body: string;
    source?: 'openrouter' | 'anthropic' | 'cursor' | 'template';
    fallback?: boolean;
    headlines?: { title: string; source: string | null; url: string }[];
  };
};

type RecentRecord = { wins: number; losses: number; ties: number; gamesPlayed: number };

export type MatchupGameContext = {
  awayTeam: string;
  homeTeam: string;
  phase?: 'pregame' | 'live' | 'final';
  awayScore?: number | null;
  homeScore?: number | null;
  period?: string | null;
  clock?: string | null;
  situation?: string | null;
  awayColor?: string | null;
  homeColor?: string | null;
  awayOdds?: number | string | null;
  homeOdds?: number | string | null;
};

export type MatchupMarket = {
  type: 'moneyline' | 'spread' | 'total';
  oddsOne: number | null;
  oddsTwo: number | null;
  spread: number | null;
  total: number | null;
};

export type MatchupGameInsights = {
  phase: 'pregame' | 'live' | 'final';
  summary?: string | null;
  events: {
    sequence: number;
    quarter: string | null;
    minute: string | null;
    teamAbbreviation: string | null;
    teamName: string | null;
    playerName: string | null;
    type: string | null;
    description: string | null;
  }[];
  eventsAvailable: boolean;
  teamStats: {
    abbreviation: string;
    totalYards: string | null;
    passingYards: string | null;
    rushingYards: string | null;
    firstDowns: string | null;
    turnovers: string | null;
    thirdDowns: string | null;
    possession: string | null;
  }[];
  teamStatsAvailable: boolean;
};

export interface MatchupAnalysisViewProps {
  game: MatchupGameContext;
  markets: MatchupMarket[];
  analysis?: MatchupAnalysis;
  insights?: MatchupGameInsights | null;
  expanded: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onToggleExpanded: () => void;
}

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatAmericanOdds = (value: unknown): string => {
  const parsed = parseNumber(value);
  if (parsed === null) return '—';
  return parsed > 0 ? `+${parsed}` : `${parsed}`;
};

const formatPoint = (value: number | null): string => {
  if (value === null) return '—';
  return value > 0 ? `+${value}` : `${value}`;
};

const formatStat = (value: number | null | undefined, suffix = ''): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}${suffix}`;
};

const formatSignedStat = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}`;
};

function splitKeysToVictory(body: string | undefined): [string, string] {
  if (!body?.trim()) return ['', ''];

  const normalized = body.replace(/\s+/g, ' ').trim();
  const [awayKey, homeKey] = normalized.split(/;\s*/, 2);
  if (awayKey && homeKey) return [awayKey.trim(), homeKey.trim()];

  const sentenceSplit = normalized.match(/^(.+?[.!?])\s+(.+)$/);
  if (sentenceSplit?.[1] && sentenceSplit[2]) return [sentenceSplit[1].trim(), sentenceSplit[2].trim()];

  const [awayPath, homePath] = normalized.split(/\s+(?:while|whereas|but)\s+/i, 2);
  if (awayPath && homePath) return [awayPath.trim(), homePath.trim()];

  return [normalized, '—'];
}

function KeysCard({
  abbreviation,
  color,
  compact,
  text,
}: {
  abbreviation: string;
  color: string;
  compact: boolean;
  text: string;
}) {
  return (
    <Box style={[styles.featureCard, !compact && styles.featureCardWide, styles.keysCard, { borderTopColor: color }]}>
      <Box style={styles.featureCardHeading}>
        <KeyRound color={color} size={19} strokeWidth={2.5} />
        <Text style={styles.featureCardTitle}>Keys to victory</Text>
        <Text style={[styles.featureTeamAbbreviation, { color }]}>{abbreviation}</Text>
      </Box>
      <Text style={styles.keySentence}>{text && text !== '—' ? text : 'No scouting key is available yet.'}</Text>
    </Box>
  );
}

function LineAndTotalCard({
  awayAbbreviation,
  awayColor,
  awayMoneyline,
  awaySpread,
  homeAbbreviation,
  homeColor,
  homeMoneyline,
  homeSpread,
  compact,
  total,
}: {
  awayAbbreviation: string;
  awayColor: string;
  awayMoneyline: number | null;
  awaySpread: number | null;
  homeAbbreviation: string;
  homeColor: string;
  homeMoneyline: number | null;
  homeSpread: number | null;
  compact: boolean;
  total: number | null;
}) {
  return (
    <Box style={[styles.featureCard, !compact && styles.featureCardWide, styles.marketCard, !compact && styles.marketCardWide]}>
      <Box style={styles.featureCardHeading}>
        <ChartNoAxesColumnIncreasing color={colors.warning[600]} size={20} strokeWidth={2.5} />
        <Text style={styles.featureCardTitle}>Line &amp; total</Text>
      </Box>
      <Box style={styles.marketTeamsRow}>
        <Box style={styles.marketTeamColumn}>
          <Text style={[styles.marketTeamTag, { backgroundColor: awayColor }]}>{awayAbbreviation}</Text>
          <Box style={styles.marketMetricsRow}>
            <Box style={styles.marketMetric}>
              <Text style={styles.marketMetricValue}>{formatAmericanOdds(awayMoneyline)}</Text>
              <Text style={styles.marketMetricLabel}>Moneyline</Text>
            </Box>
            <Box style={[styles.marketMetric, styles.marketMetricSeparated]}>
              <Text style={styles.marketMetricValue}>{formatPoint(awaySpread)}</Text>
              <Text style={styles.marketMetricLabel}>Spread</Text>
            </Box>
          </Box>
        </Box>
        <Box style={[styles.marketTeamColumn, styles.marketTeamColumnRight]}>
          <Text style={[styles.marketTeamTag, { backgroundColor: homeColor }]}>{homeAbbreviation}</Text>
          <Box style={styles.marketMetricsRow}>
            <Box style={styles.marketMetric}>
              <Text style={styles.marketMetricValue}>{formatAmericanOdds(homeMoneyline)}</Text>
              <Text style={styles.marketMetricLabel}>Moneyline</Text>
            </Box>
            <Box style={[styles.marketMetric, styles.marketMetricSeparated]}>
              <Text style={styles.marketMetricValue}>{formatPoint(homeSpread)}</Text>
              <Text style={styles.marketMetricLabel}>Spread</Text>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box style={styles.gameTotal}>
        <Text style={styles.gameTotalLabel}>Game total · O/U</Text>
        <Text style={styles.gameTotalValue}>{total === null ? '—' : total}</Text>
      </Box>
    </Box>
  );
}

function MarketSummary({
  game,
  markets,
  analysis,
  compact,
}: {
  game: MatchupGameContext;
  markets: MatchupMarket[];
  analysis?: MatchupAnalysis;
  compact: boolean;
}) {
  const moneyline = markets.find((market) => market.type === 'moneyline');
  const spread = markets.find((market) => market.type === 'spread');
  const total = markets.find((market) => market.type === 'total');
  const homeMoneyline = parseNumber(game.homeOdds) ?? moneyline?.oddsOne ?? null;
  const awayMoneyline = parseNumber(game.awayOdds) ?? moneyline?.oddsTwo ?? null;
  const homeSpread = spread?.spread ?? null;
  const awaySpread = homeSpread === null ? null : -homeSpread;
  const storyBody = analysis?.story?.source === 'template' && !analysis.story.fallback
    ? undefined
    : analysis?.story?.body;
  const [awayKey, homeKey] = splitKeysToVictory(storyBody);
  const awayColor = game.awayColor ?? colors.ink.field;
  const homeColor = game.homeColor ?? colors.ink.pressMuted;

  const lineCard = (
    <LineAndTotalCard
      awayAbbreviation={game.awayTeam}
      awayColor={awayColor}
      awayMoneyline={awayMoneyline}
      awaySpread={awaySpread}
      homeAbbreviation={game.homeTeam}
      homeColor={homeColor}
      homeMoneyline={homeMoneyline}
      homeSpread={homeSpread}
      compact={compact}
      total={total?.total ?? null}
    />
  );
  const awayKeysCard = <KeysCard abbreviation={game.awayTeam} color={awayColor} compact={compact} text={awayKey} />;
  const homeKeysCard = <KeysCard abbreviation={game.homeTeam} color={homeColor} compact={compact} text={homeKey} />;

  return (
    <Box style={styles.featureGrid}>
      {compact ? lineCard : awayKeysCard}
      {compact ? awayKeysCard : lineCard}
      {homeKeysCard}
    </Box>
  );
}

const formatInjuryStatus = (status: string | null): string | null => {
  if (!status) return null;
  return /^I\.?L\.?$/i.test(status.trim()) ? 'IR' : status;
};

const formatRecentRecord = (record: RecentRecord | null | undefined): string => {
  if (!record) return '—';
  return `${record.wins}-${record.losses}${record.ties ? `-${record.ties}` : ''}`;
};

const recordStrength = (record: RecentRecord | null | undefined): number | null => {
  if (!record || record.gamesPlayed === 0) return null;
  return (record.wins + record.ties * 0.5) / record.gamesPlayed;
};

const rankStrength = (rank: number | null | undefined): number | null =>
  rank === null || rank === undefined ? null : Math.max(0, 33 - rank);

const statStrength = (value: number | null, otherValue: number | null, lowerIsBetter = false): number | null => {
  if (value === null) return null;
  if (otherValue === null || value === otherValue) return 1;
  if (!lowerIsBetter) return Math.max(0.1, value);
  return Math.max(0.1, Math.max(value, otherValue) - value + 1);
};

const signedStatStrength = (value: number | null, otherValue: number | null): number | null => {
  if (value === null) return null;
  if (otherValue === null || value === otherValue) return 1;
  return Math.max(0.1, value - Math.min(value, otherValue) + 1);
};

type ComparisonRow = {
  label: string;
  awayDisplay: string;
  homeDisplay: string;
  awayStrength: number | null;
  homeStrength: number | null;
};

function ComparisonBar({ row, awayColor, homeColor }: { row: ComparisonRow; awayColor: string; homeColor: string }) {
  const maxStrength = Math.max(row.awayStrength ?? 0, row.homeStrength ?? 0, 1);
  const awayWidth = row.awayStrength === null ? 0 : (row.awayStrength / maxStrength) * 100;
  const homeWidth = row.homeStrength === null ? 0 : (row.homeStrength / maxStrength) * 100;

  return (
    <Box style={styles.barRow}>
      <Box style={styles.barValueRow}>
        <Text style={styles.barValue}>{row.awayDisplay}</Text>
        <Text style={styles.barLabel} numberOfLines={2}>{row.label}</Text>
        <Text style={[styles.barValue, styles.barValueRight]}>{row.homeDisplay}</Text>
      </Box>
      <Box style={styles.barTrack}>
        <Box style={[styles.barHalf, styles.barHalfLeft]}>
          <Box style={[styles.barFill, { backgroundColor: awayColor, width: `${awayWidth}%` }]} />
        </Box>
        <Box style={styles.barCenter} />
        <Box style={[styles.barHalf, styles.barHalfRight]}>
          <Box style={[styles.barFill, { backgroundColor: homeColor, width: `${homeWidth}%` }]} />
        </Box>
      </Box>
    </Box>
  );
}

function TeamComparison({ analysis, game }: { analysis: MatchupAnalysis; game: MatchupGameContext }) {
  const awayRating = analysis.powerRatings?.awayTeam ?? null;
  const homeRating = analysis.powerRatings?.homeTeam ?? null;
  const awayColor = game.awayColor ?? '#1F7A4D';
  const homeColor = game.homeColor ?? '#3F6E7D';
  // Older cached snapshots stored season totals and did not include lastFive.
  // Keep those totals out of the new per-game UI until the snapshot refreshes.
  const hasPerGameStats = analysis.awayTeam.lastFive !== undefined && analysis.homeTeam.lastFive !== undefined;
  const hasSeasonStats = Boolean(analysis.awayTeam.seasonStats || analysis.homeTeam.seasonStats);
  const hasRecentStats = Boolean(analysis.awayTeam.recentStats || analysis.homeTeam.recentStats);
  const awayPpg = hasPerGameStats ? analysis.awayTeam.offense.points.value : null;
  const homePpg = hasPerGameStats ? analysis.homeTeam.offense.points.value : null;
  const awayAllowed = hasPerGameStats ? analysis.awayTeam.defense.points.value : null;
  const homeAllowed = hasPerGameStats ? analysis.homeTeam.defense.points.value : null;
  const rows: ComparisonRow[] = [
    {
      label: 'Overall power ranking',
      awayDisplay: awayRating ? `#${awayRating.overall.rank}` : '—',
      homeDisplay: homeRating ? `#${homeRating.overall.rank}` : '—',
      awayStrength: rankStrength(awayRating?.overall.rank),
      homeStrength: rankStrength(homeRating?.overall.rank),
    },
    {
      label: 'Offensive power ranking',
      awayDisplay: awayRating ? `#${awayRating.offense.rank}` : '—',
      homeDisplay: homeRating ? `#${homeRating.offense.rank}` : '—',
      awayStrength: rankStrength(awayRating?.offense.rank),
      homeStrength: rankStrength(homeRating?.offense.rank),
    },
    {
      label: 'Offensive points per game',
      awayDisplay: awayPpg === null ? '—' : `${awayPpg.toFixed(1)} PPG`,
      homeDisplay: homePpg === null ? '—' : `${homePpg.toFixed(1)} PPG`,
      awayStrength: statStrength(awayPpg, homePpg),
      homeStrength: statStrength(homePpg, awayPpg),
    },
    {
      label: 'Defensive power ranking',
      awayDisplay: awayRating ? `#${awayRating.defense.rank}` : '—',
      homeDisplay: homeRating ? `#${homeRating.defense.rank}` : '—',
      awayStrength: rankStrength(awayRating?.defense.rank),
      homeStrength: rankStrength(homeRating?.defense.rank),
    },
    {
      label: 'Defensive points allowed per game',
      awayDisplay: awayAllowed === null ? '—' : `${awayAllowed.toFixed(1)} PPG`,
      homeDisplay: homeAllowed === null ? '—' : `${homeAllowed.toFixed(1)} PPG`,
      awayStrength: statStrength(awayAllowed, homeAllowed, true),
      homeStrength: statStrength(homeAllowed, awayAllowed, true),
    },
    {
      label: 'Last 5 games record',
      awayDisplay: formatRecentRecord(analysis.awayTeam.lastFive?.record),
      homeDisplay: formatRecentRecord(analysis.homeTeam.lastFive?.record),
      awayStrength: recordStrength(analysis.awayTeam.lastFive?.record),
      homeStrength: recordStrength(analysis.homeTeam.lastFive?.record),
    },
    {
      label: 'Last 5 games against the spread',
      awayDisplay: formatRecentRecord(analysis.awayTeam.lastFive?.ats),
      homeDisplay: formatRecentRecord(analysis.homeTeam.lastFive?.ats),
      awayStrength: recordStrength(analysis.awayTeam.lastFive?.ats),
      homeStrength: recordStrength(analysis.homeTeam.lastFive?.ats),
    },
    ...(hasRecentStats ? [
      {
        label: 'Last 5 points per game',
        awayDisplay: formatStat(analysis.awayTeam.recentStats?.pointsPerGame),
        homeDisplay: formatStat(analysis.homeTeam.recentStats?.pointsPerGame),
        awayStrength: statStrength(analysis.awayTeam.recentStats?.pointsPerGame ?? null, analysis.homeTeam.recentStats?.pointsPerGame ?? null),
        homeStrength: statStrength(analysis.homeTeam.recentStats?.pointsPerGame ?? null, analysis.awayTeam.recentStats?.pointsPerGame ?? null),
      },
      {
        label: 'Last 5 total yards per game',
        awayDisplay: formatStat(analysis.awayTeam.recentStats?.totalYardsPerGame),
        homeDisplay: formatStat(analysis.homeTeam.recentStats?.totalYardsPerGame),
        awayStrength: statStrength(analysis.awayTeam.recentStats?.totalYardsPerGame ?? null, analysis.homeTeam.recentStats?.totalYardsPerGame ?? null),
        homeStrength: statStrength(analysis.homeTeam.recentStats?.totalYardsPerGame ?? null, analysis.awayTeam.recentStats?.totalYardsPerGame ?? null),
      },
    ] : []),
    ...(hasSeasonStats ? [
      {
        label: 'Total yards per game',
        awayDisplay: formatStat(analysis.awayTeam.seasonStats?.totalYardsPerGame),
        homeDisplay: formatStat(analysis.homeTeam.seasonStats?.totalYardsPerGame),
        awayStrength: statStrength(analysis.awayTeam.seasonStats?.totalYardsPerGame ?? null, analysis.homeTeam.seasonStats?.totalYardsPerGame ?? null),
        homeStrength: statStrength(analysis.homeTeam.seasonStats?.totalYardsPerGame ?? null, analysis.awayTeam.seasonStats?.totalYardsPerGame ?? null),
      },
      {
        label: 'Total yards allowed per game',
        awayDisplay: formatStat(analysis.awayTeam.seasonStats?.totalYardsAllowedPerGame),
        homeDisplay: formatStat(analysis.homeTeam.seasonStats?.totalYardsAllowedPerGame),
        awayStrength: statStrength(analysis.awayTeam.seasonStats?.totalYardsAllowedPerGame ?? null, analysis.homeTeam.seasonStats?.totalYardsAllowedPerGame ?? null, true),
        homeStrength: statStrength(analysis.homeTeam.seasonStats?.totalYardsAllowedPerGame ?? null, analysis.awayTeam.seasonStats?.totalYardsAllowedPerGame ?? null, true),
      },
      {
        label: 'Passing yards per game',
        awayDisplay: formatStat(analysis.awayTeam.seasonStats?.passingYardsPerGame),
        homeDisplay: formatStat(analysis.homeTeam.seasonStats?.passingYardsPerGame),
        awayStrength: statStrength(analysis.awayTeam.seasonStats?.passingYardsPerGame ?? null, analysis.homeTeam.seasonStats?.passingYardsPerGame ?? null),
        homeStrength: statStrength(analysis.homeTeam.seasonStats?.passingYardsPerGame ?? null, analysis.awayTeam.seasonStats?.passingYardsPerGame ?? null),
      },
      {
        label: 'Rushing yards per game',
        awayDisplay: formatStat(analysis.awayTeam.seasonStats?.rushingYardsPerGame),
        homeDisplay: formatStat(analysis.homeTeam.seasonStats?.rushingYardsPerGame),
        awayStrength: statStrength(analysis.awayTeam.seasonStats?.rushingYardsPerGame ?? null, analysis.homeTeam.seasonStats?.rushingYardsPerGame ?? null),
        homeStrength: statStrength(analysis.homeTeam.seasonStats?.rushingYardsPerGame ?? null, analysis.awayTeam.seasonStats?.rushingYardsPerGame ?? null),
      },
      {
        label: 'Turnover differential',
        awayDisplay: formatSignedStat(analysis.awayTeam.seasonStats?.turnoverDifferential),
        homeDisplay: formatSignedStat(analysis.homeTeam.seasonStats?.turnoverDifferential),
        awayStrength: signedStatStrength(analysis.awayTeam.seasonStats?.turnoverDifferential ?? null, analysis.homeTeam.seasonStats?.turnoverDifferential ?? null),
        homeStrength: signedStatStrength(analysis.homeTeam.seasonStats?.turnoverDifferential ?? null, analysis.awayTeam.seasonStats?.turnoverDifferential ?? null),
      },
      {
        label: 'Third-down conversion',
        awayDisplay: formatStat(analysis.awayTeam.seasonStats?.thirdDownConversionPct, '%'),
        homeDisplay: formatStat(analysis.homeTeam.seasonStats?.thirdDownConversionPct, '%'),
        awayStrength: statStrength(analysis.awayTeam.seasonStats?.thirdDownConversionPct ?? null, analysis.homeTeam.seasonStats?.thirdDownConversionPct ?? null),
        homeStrength: statStrength(analysis.homeTeam.seasonStats?.thirdDownConversionPct ?? null, analysis.awayTeam.seasonStats?.thirdDownConversionPct ?? null),
      },
      {
        label: 'Red-zone scoring',
        awayDisplay: formatStat(analysis.awayTeam.seasonStats?.redZoneScorePct, '%'),
        homeDisplay: formatStat(analysis.homeTeam.seasonStats?.redZoneScorePct, '%'),
        awayStrength: statStrength(analysis.awayTeam.seasonStats?.redZoneScorePct ?? null, analysis.homeTeam.seasonStats?.redZoneScorePct ?? null),
        homeStrength: statStrength(analysis.homeTeam.seasonStats?.redZoneScorePct ?? null, analysis.awayTeam.seasonStats?.redZoneScorePct ?? null),
      },
    ] : []),
  ];

  return (
    <Box style={styles.comparisonSection}>
      <Box style={styles.comparisonTitleRow}>
        <Text style={styles.comparisonTitle}>Team comparison</Text>
        <Box style={styles.teamTags}>
          <Text style={[styles.teamTag, { backgroundColor: awayColor }]}>{game.awayTeam}</Text>
          <Text style={[styles.teamTag, { backgroundColor: homeColor }]}>{game.homeTeam}</Text>
        </Box>
      </Box>
      {rows.map((row) => <ComparisonBar key={row.label} row={row} awayColor={awayColor} homeColor={homeColor} />)}
      <Text style={styles.comparisonMeta}>
        {analysis.powerRatings?.sourceLabel === 'Preseason baseline' ? 'Power rankings use a preseason baseline.' : 'Power rankings update as completed games are added.'}
        {analysis.dataSource === 'balldontlie' ? ' BALLDONTLIE supplies the season and recent-game stats.' : ''}
      </Text>
    </Box>
  );
}

function InjuryList({ team }: { team: MatchupTeam }) {
  return (
    <Box style={styles.injuryColumn}>
      <Text style={styles.injuryTeam}>{team.abbreviation} INJURIES</Text>
      {team.injuries.length === 0 ? (
        <Text style={styles.noInjuries}>No current report</Text>
      ) : (
        team.injuries.slice(0, 5).map((injury) => (
          <Box key={`${injury.playerName}-${injury.status ?? ''}`} style={styles.injuryRow}>
            <Text style={styles.injuryName} numberOfLines={1}>
              {injury.playerName}{injury.position ? ` · ${injury.position}` : ''}
            </Text>
            <Text style={styles.injuryMeta} numberOfLines={1}>
              {formatInjuryStatus(injury.status) || injury.description || 'Reported'}
            </Text>
          </Box>
        ))
      )}
    </Box>
  );
}

function getEventTitle(event: MatchupGameInsights['events'][number]): string {
  if (event.description) return event.description;
  const parts = [event.playerName, event.type?.toUpperCase()].filter(Boolean);
  return parts.join(' · ') || 'Scoring play';
}

function getEventMeta(event: MatchupGameInsights['events'][number]): string {
  return [event.teamAbbreviation ?? event.teamName, event.quarter, event.minute]
    .filter(Boolean)
    .join(' · ');
}

function getCurrentScoreStory(game: MatchupGameContext): string {
  const awayScore = game.awayScore;
  const homeScore = game.homeScore;
  if (typeof awayScore !== 'number' || typeof homeScore !== 'number') {
    return 'The game is underway. Waiting for the first score update.';
  }
  if (awayScore === homeScore) return `${game.awayTeam} and ${game.homeTeam} are tied ${awayScore}–${homeScore}.`;
  const leader = awayScore > homeScore ? game.awayTeam : game.homeTeam;
  const margin = Math.abs(awayScore - homeScore);
  return margin <= 8
    ? `${leader} lead by ${margin} in a one-score game.`
    : `${leader} lead by ${margin}.`;
}

function getStatStorylines(
  game: MatchupGameContext,
  insights: MatchupGameInsights,
): string[] {
  const away = insights.teamStats.find((team) => team.abbreviation === game.awayTeam);
  const home = insights.teamStats.find((team) => team.abbreviation === game.homeTeam);
  if (!away || !home) return [];

  const storylines: string[] = [];
  const awayYards = parseNumber(away.totalYards);
  const homeYards = parseNumber(home.totalYards);
  if (awayYards !== null && homeYards !== null && Math.abs(awayYards - homeYards) >= 20) {
    const leader = awayYards > homeYards ? game.awayTeam : game.homeTeam;
    const margin = Math.abs(awayYards - homeYards);
    storylines.push(`${leader} have gained ${margin} more yards.`);
  }

  const awayTurnovers = parseNumber(away.turnovers);
  const homeTurnovers = parseNumber(home.turnovers);
  if (awayTurnovers !== null && homeTurnovers !== null && awayTurnovers !== homeTurnovers) {
    const fewerTurnovers = awayTurnovers < homeTurnovers ? game.awayTeam : game.homeTeam;
    storylines.push(`${fewerTurnovers} have protected the ball better.`);
  }
  return storylines;
}

function GameScoreStrip({ game, phase }: { game: MatchupGameContext; phase: 'live' | 'final' }) {
  return (
    <Box style={styles.gameScoreStrip}>
      <Box style={styles.gameScoreMeta}>
        <Text style={phase === 'live' ? styles.liveStatus : styles.finalStatus}>{phase === 'live' ? 'LIVE' : 'FINAL'}</Text>
        {phase === 'live' && (game.period || game.clock) ? (
          <Text style={styles.gameClock}>{[game.period, game.clock].filter(Boolean).join(' · ')}</Text>
        ) : null}
      </Box>
      <Box style={styles.gameScoreRow}>
        <Box style={styles.gameScoreTeam}>
          <Text style={styles.gameScoreAbbr}>{game.awayTeam}</Text>
          <Text style={styles.gameScoreNumber}>{typeof game.awayScore === 'number' ? game.awayScore : '—'}</Text>
        </Box>
        <Text style={styles.gameScoreDivider}>–</Text>
        <Box style={[styles.gameScoreTeam, styles.gameScoreTeamRight]}>
          <Text style={[styles.gameScoreNumber, styles.gameScoreNumberRight]}>{typeof game.homeScore === 'number' ? game.homeScore : '—'}</Text>
          <Text style={styles.gameScoreAbbr}>{game.homeTeam}</Text>
        </Box>
      </Box>
      {phase === 'live' && game.situation ? <Text style={styles.gameSituation}>{game.situation}</Text> : null}
    </Box>
  );
}

function KeyPlayList({
  insights,
  phase,
}: {
  insights: MatchupGameInsights;
  phase: 'live' | 'final';
}) {
  const events = [...insights.events].sort((left, right) => right.sequence - left.sequence).slice(0, 6);
  return (
    <Box style={styles.insightSection}>
      <Text style={styles.insightSectionTitle}>{phase === 'final' ? 'Scoring plays' : 'Key plays'}</Text>
      {events.length > 0 ? events.map((event) => (
        <Box key={`${event.sequence}-${event.type ?? 'play'}`} style={styles.keyPlayRow}>
          <Text style={styles.keyPlayMeta}>{getEventMeta(event) || `PLAY ${event.sequence}`}</Text>
          <Text style={styles.keyPlayTitle}>{getEventTitle(event)}</Text>
        </Box>
      )) : (
        <Text style={styles.unavailable}>
          {insights.eventsAvailable
            ? 'No scoring plays have been posted yet.'
            : phase === 'live' ? 'Live play updates are unavailable right now.' : 'Scoring play details are unavailable.'}
        </Text>
      )}
    </Box>
  );
}

function TeamGameStats({
  game,
  insights,
}: {
  game: MatchupGameContext;
  insights: MatchupGameInsights;
}) {
  const away = insights.teamStats.find((team) => team.abbreviation === game.awayTeam);
  const home = insights.teamStats.find((team) => team.abbreviation === game.homeTeam);
  const rows = [
    { label: 'Total yards', away: away?.totalYards, home: home?.totalYards },
    { label: 'Passing yards', away: away?.passingYards, home: home?.passingYards },
    { label: 'Rushing yards', away: away?.rushingYards, home: home?.rushingYards },
    { label: 'First downs', away: away?.firstDowns, home: home?.firstDowns },
    { label: 'Turnovers', away: away?.turnovers, home: home?.turnovers },
    { label: 'Third down', away: away?.thirdDowns, home: home?.thirdDowns },
    { label: 'Possession', away: away?.possession, home: home?.possession },
  ].filter((row) => row.away !== null && row.away !== undefined || row.home !== null && row.home !== undefined);

  return (
    <Box style={styles.insightSection}>
      <Box style={styles.teamStatsHeading}>
        <Text style={styles.insightSectionTitle}>Team stats</Text>
        <Box style={styles.teamStatsColumns}>
          <Text style={[styles.teamStatsAbbr, { color: game.awayColor ?? '#1F7A4D' }]}>{game.awayTeam}</Text>
          <Text style={[styles.teamStatsAbbr, { color: game.homeColor ?? '#3F6E7D' }]}>{game.homeTeam}</Text>
        </Box>
      </Box>
      {rows.length > 0 ? rows.map((row) => (
        <Box key={row.label} style={styles.teamStatsRow}>
          <Text style={styles.teamStatsValue}>{row.away ?? '—'}</Text>
          <Text style={styles.teamStatsLabel}>{row.label}</Text>
          <Text style={[styles.teamStatsValue, styles.teamStatsValueRight]}>{row.home ?? '—'}</Text>
        </Box>
      )) : (
        <Text style={styles.unavailable}>
          {insights.teamStatsAvailable ? 'Team stat details have not been posted yet.' : 'Team statistics are unavailable from the provider.'}
        </Text>
      )}
    </Box>
  );
}

function LiveGameCenter({ game, insights }: { game: MatchupGameContext; insights?: MatchupGameInsights }) {
  const storylines = insights ? getStatStorylines(game, insights) : [];

  return (
    <Box testID="matchup-live-center">
      <GameScoreStrip game={game} phase="live" />
      <Box style={styles.insightSection}>
        <Text style={styles.insightSectionTitle}>Storylines</Text>
        <Text style={styles.storylineText}>{getCurrentScoreStory(game)}</Text>
        {storylines.map((storyline) => <Text key={storyline} style={styles.storylineText}>{storyline}</Text>)}
      </Box>
      {insights ? <KeyPlayList insights={insights} phase="live" /> : null}
      {insights?.teamStatsAvailable ? <TeamGameStats game={game} insights={insights} /> : null}
    </Box>
  );
}

function getFinalSummary(game: MatchupGameContext, insights: MatchupGameInsights | undefined): string {
  if (typeof game.awayScore !== 'number' || typeof game.homeScore !== 'number') {
    return 'Final score details are not available yet.';
  }
  if (game.awayScore === game.homeScore) {
    return `${game.awayTeam} and ${game.homeTeam} finished level.`;
  }

  const awayWon = game.awayScore > game.homeScore;
  const winner = awayWon ? game.awayTeam : game.homeTeam;
  const loser = awayWon ? game.homeTeam : game.awayTeam;
  const margin = Math.abs(game.homeScore - game.awayScore);
  const stats = insights?.teamStats.find((team) => team.abbreviation === winner);
  const opposingStats = insights?.teamStats.find((team) => team.abbreviation === loser);
  const winnerYards = stats ? parseNumber(stats.totalYards) : null;
  const loserYards = opposingStats ? parseNumber(opposingStats.totalYards) : null;
  const yards = winnerYards !== null && loserYards !== null ? winnerYards - loserYards : null;
  const yardsLine = yards !== null && yards >= 25
    ? ` ${winner} also outgained ${loser} by ${yards} yards.`
    : '';

  return `${winner} beat ${loser} by ${margin}.${yardsLine}`;
}

function FinalGameRecap({ game, insights }: { game: MatchupGameContext; insights?: MatchupGameInsights }) {
  return (
    <Box testID="matchup-final-recap">
      <GameScoreStrip game={game} phase="final" />
      <Box style={styles.insightSection}>
        <Text style={styles.insightSectionTitle}>Game summary</Text>
        <Text style={styles.finalSummaryText} numberOfLines={3} ellipsizeMode="tail">
          {insights?.summary || getFinalSummary(game, insights)}
        </Text>
      </Box>
      {insights ? <TeamGameStats game={game} insights={insights} /> : null}
      {insights ? <KeyPlayList insights={insights} phase="final" /> : null}
    </Box>
  );
}

export function MatchupAnalysisPanel({
  gameId,
  game,
  markets,
}: {
  gameId: string;
  game: MatchupGameContext;
  markets: MatchupMarket[];
}) {
  const [expanded, setExpanded] = useState(false);
  const phase = game.phase ?? 'pregame';
  const matchupQuery = useQuery(
    trpc.nfl.matchupAnalysis.queryOptions(
      { gameId },
      {
        enabled: expanded && phase === 'pregame',
        staleTime: 15 * 60 * 1000,
      },
    ),
  );
  const insightPhase = phase === 'final' ? 'final' : 'live';
  const insightsQuery = useQuery(
    trpc.nfl.gameInsights.queryOptions(
      { gameId, phase: insightPhase },
      {
        enabled: expanded && phase !== 'pregame',
        staleTime: phase === 'live' ? 15 * 1000 : 15 * 60 * 1000,
        refetchInterval: phase === 'live' ? 30 * 1000 : false,
      },
    ),
  );
  const activeQuery = phase === 'pregame' ? matchupQuery : insightsQuery;

  return (
    <MatchupAnalysisView
      game={game}
      markets={markets}
      analysis={matchupQuery.data}
      insights={insightsQuery.data}
      expanded={expanded}
      isLoading={activeQuery.isLoading}
      isError={activeQuery.isError}
      onToggleExpanded={() => setExpanded((value) => !value)}
    />
  );
}

/** Pure display slice for deterministic visual review without the API query. */
export function MatchupAnalysisView({
  game,
  markets,
  analysis,
  insights,
  expanded,
  isLoading = false,
  isError = false,
  onToggleExpanded,
}: MatchupAnalysisViewProps) {
  const theme = useAppTheme();
  const { width } = useWindowDimensions();
  const compactLayout = width < theme.breakpoints.md;
  const phase = game.phase ?? 'pregame';
  const title = phase === 'live' ? 'Live game center' : phase === 'final' ? 'Game recap' : 'Scouting report';
  const summaryMarkets = analysis?.markets?.length ? analysis.markets : markets;

  return (
    <Box style={[styles.panel, !expanded && styles.panelCollapsed]}>
      <PressableBox
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={`Toggle ${title.toLowerCase()}`}
        onPress={onToggleExpanded}
        style={[styles.toggle, !expanded && styles.toggleCollapsed]}
      >
        <Box style={styles.titleRow}>
          <Text style={styles.title}>{title}</Text>
          {phase === 'pregame' ? <Text style={styles.beta}>Beta</Text> : null}
        </Box>
        {expanded ? <ChevronUp color={theme.colors.press} size={20} /> : <ChevronDown color={theme.colors.press} size={20} />}
      </PressableBox>

      {expanded ? (
        <Box style={styles.content}>
          {phase === 'pregame' ? (
            <>
              <MarketSummary game={game} markets={summaryMarkets} analysis={analysis} compact={compactLayout} />
              {isLoading ? <Text style={styles.loading}>Loading scouting report…</Text> : null}
              {isError ? <Text style={styles.error}>Matchup analysis is temporarily unavailable.</Text> : null}
              {analysis ? (
                <>
                  <TeamComparison analysis={analysis} game={game} />

                  <Box style={styles.injurySection}>
                    <Text style={styles.injuryTitle}>Injury report</Text>
                    {analysis.injuriesAvailable ? (
                      <Box style={styles.injuryGrid}>
                        <InjuryList team={analysis.awayTeam} />
                        <InjuryList team={analysis.homeTeam} />
                      </Box>
                    ) : <Text style={styles.unavailable}>Current injury reports are not available from the provider.</Text>}
                  </Box>
                </>
              ) : null}
            </>
          ) : phase === 'live' ? (
            <>
              {isLoading ? <Text style={styles.loading}>Loading live game updates…</Text> : null}
              {isError ? <Text style={styles.error}>Live play details are temporarily unavailable.</Text> : null}
              <LiveGameCenter game={game} insights={insights ?? undefined} />
            </>
          ) : (
            <>
              {isLoading ? <Text style={styles.loading}>Loading final game details…</Text> : null}
              {isError ? <Text style={styles.error}>Final game details are temporarily unavailable.</Text> : null}
              <FinalGameRecap game={game} insights={insights ?? undefined} />
            </>
          )}
        </Box>
      ) : null}
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  barCenter: { backgroundColor: theme.colors.press, height: '100%', width: 3 },
  barFill: { height: '100%' },
  barHalf: { backgroundColor: theme.colors.neutral100, flex: 1, height: 10, justifyContent: 'center', overflow: 'hidden' },
  barHalfLeft: { alignItems: 'flex-end' },
  barHalfRight: { alignItems: 'flex-start' },
  barLabel: StyleSheet.flatten([theme.textVariants.caption, { color: theme.colors.text, flex: 1.5, fontSize: 9, textAlign: 'center' }]),
  barRow: { marginTop: theme.spacing.s },
  barTrack: { alignItems: 'center', borderColor: theme.colors.press, borderWidth: 2, flexDirection: 'row', height: 14, overflow: 'hidden' },
  barValue: StyleSheet.flatten([theme.textVariants.button, { color: theme.colors.press, flex: 1, fontSize: 11 }]),
  barValueRight: { textAlign: 'right' },
  barValueRow: { alignItems: 'center', flexDirection: 'row', marginBottom: 3 },
  beta: StyleSheet.flatten([theme.textVariants.caption, { color: colors.warning[300], fontSize: 10, fontWeight: '800', letterSpacing: 0.4, textTransform: 'uppercase' }]),
  comparisonMeta: StyleSheet.flatten([theme.textVariants.caption, { color: theme.colors.textSecondary, fontSize: 9, marginTop: theme.spacing.s }]),
  comparisonSection: { borderColor: theme.colors.border, borderWidth: 1, marginTop: theme.spacing.l, padding: theme.spacing.s },
  comparisonTitle: StyleSheet.flatten([theme.textVariants.button, { color: theme.colors.press, fontSize: 15 }]),
  comparisonTitleRow: { alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' },
  content: { borderTopColor: theme.colors.border, borderTopWidth: 1, padding: theme.spacing.m },
  error: StyleSheet.flatten([theme.textVariants.bodySmall, { color: theme.colors.error }]),
  featureCard: { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, borderRadius: theme.borderRadii.m, borderWidth: 1, padding: theme.spacing.m },
  featureCardHeading: { alignItems: 'center', borderBottomColor: theme.colors.border, borderBottomWidth: 1, flexDirection: 'row', gap: theme.spacing.xs, marginBottom: theme.spacing.s, paddingBottom: theme.spacing.s },
  featureCardTitle: StyleSheet.flatten([theme.textVariants.button, { color: theme.colors.press, flexShrink: 1, fontSize: 14, textTransform: 'uppercase' }]),
  featureCardWide: { flex: 1 },
  featureGrid: { flexDirection: { xs: 'column', md: 'row' }, gap: theme.spacing.s, marginTop: theme.spacing.s },
  featureTeamAbbreviation: StyleSheet.flatten([theme.textVariants.caption, { fontSize: 10, fontWeight: '800', letterSpacing: 0.7, marginLeft: 'auto' }]),
  finalStatus: StyleSheet.flatten([theme.textVariants.caption, { color: '#CBE5D5', fontSize: 10, fontWeight: '800', letterSpacing: 1.2 }]),
  finalSummaryText: StyleSheet.flatten([theme.textVariants.bodySmall, { color: theme.colors.text, fontWeight: '700', marginTop: theme.spacing.xs }]),
  gameClock: StyleSheet.flatten([theme.textVariants.caption, { color: '#E7EFE9', fontSize: 10 }]),
  gameScoreAbbr: StyleSheet.flatten([theme.textVariants.caption, { color: '#E7EFE9', fontWeight: '800', letterSpacing: 0.8 }]),
  gameScoreDivider: StyleSheet.flatten([theme.textVariants.h3, { color: '#9DAB9F', fontSize: 22, marginHorizontal: theme.spacing.s }]),
  gameScoreMeta: { alignItems: 'center', flexDirection: 'row', gap: theme.spacing.s, justifyContent: 'space-between' },
  gameScoreNumber: StyleSheet.flatten([theme.textVariants.h3, { color: '#FFFEFA', fontSize: 30, lineHeight: 34, minWidth: 34 }]),
  gameScoreNumberRight: { textAlign: 'right' },
  gameScoreRow: { alignItems: 'center', flexDirection: 'row', marginTop: theme.spacing.xs },
  gameScoreStrip: { backgroundColor: theme.colors.press, paddingHorizontal: theme.spacing.m, paddingVertical: theme.spacing.s },
  gameScoreTeam: { alignItems: 'center', flex: 1, flexDirection: 'row', gap: theme.spacing.s },
  gameScoreTeamRight: { justifyContent: 'flex-end' },
  gameSituation: StyleSheet.flatten([theme.textVariants.caption, { color: '#C9D3CB', fontSize: 10, marginTop: theme.spacing.xs }]),
  gameTotal: { alignItems: 'center', backgroundColor: theme.colors.neutral100, borderRadius: theme.borderRadii.s, flexDirection: 'row', justifyContent: 'space-between', marginTop: theme.spacing.m, paddingHorizontal: theme.spacing.m, paddingVertical: theme.spacing.xs },
  gameTotalLabel: StyleSheet.flatten([theme.textVariants.caption, { color: theme.colors.textSecondary, fontSize: 9, fontWeight: '800', letterSpacing: 0.7, textTransform: 'uppercase' }]),
  gameTotalValue: StyleSheet.flatten([theme.textVariants.h3, { color: theme.colors.press, fontSize: 23, lineHeight: 28 }]),
  injuryColumn: { borderColor: theme.colors.border, borderWidth: 1, flex: 1, padding: theme.spacing.s },
  injuryGrid: { flexDirection: { xs: 'column', sm: 'row' }, gap: theme.spacing.s, marginTop: theme.spacing.s },
  injuryMeta: StyleSheet.flatten([theme.textVariants.caption, { fontSize: 9, letterSpacing: 0.1, marginTop: 1 }]),
  injuryName: StyleSheet.flatten([theme.textVariants.bodySmall, { fontWeight: '700' }]),
  injuryRow: { marginTop: theme.spacing.xs },
  injurySection: { borderTopColor: theme.colors.border, borderTopWidth: 1, marginTop: theme.spacing.l, paddingTop: theme.spacing.s },
  injuryTeam: StyleSheet.flatten([theme.textVariants.caption, { color: theme.colors.press }]),
  injuryTitle: StyleSheet.flatten([theme.textVariants.button, { fontSize: 15 }]),
  insightSection: { borderColor: theme.colors.border, borderWidth: 1, marginTop: theme.spacing.s, padding: theme.spacing.s },
  insightSectionTitle: StyleSheet.flatten([theme.textVariants.caption, { color: theme.colors.press, fontWeight: '800', letterSpacing: 0.8, textTransform: 'uppercase' }]),
  keyPlayMeta: StyleSheet.flatten([theme.textVariants.caption, { color: theme.colors.textSecondary, fontSize: 9, letterSpacing: 0.3 }]),
  keyPlayRow: { borderTopColor: theme.colors.border, borderTopWidth: 1, marginTop: theme.spacing.xs, paddingTop: theme.spacing.xs },
  keyPlayTitle: StyleSheet.flatten([theme.textVariants.bodySmall, { fontWeight: '700', marginTop: 2 }]),
  keySentence: StyleSheet.flatten([theme.textVariants.body, { color: theme.colors.text, marginTop: theme.spacing.s }]),
  keysCard: { borderTopColor: colors.warning[600], borderTopWidth: 3 },
  liveStatus: StyleSheet.flatten([theme.textVariants.caption, { color: colors.warning[300], fontSize: 10, fontWeight: '800', letterSpacing: 1.2 }]),
  loading: StyleSheet.flatten([theme.textVariants.caption, { color: theme.colors.press }]),
  marketCard: { borderTopColor: colors.warning[600], borderTopWidth: 3 },
  marketCardWide: { flex: 1.12 },
  marketMetric: { alignItems: 'center', flex: 1, minWidth: 0 },
  marketMetricLabel: StyleSheet.flatten([theme.textVariants.caption, { color: theme.colors.textSecondary, fontSize: 9, letterSpacing: 0.3, marginTop: 2, textTransform: 'uppercase' }]),
  marketMetricSeparated: { borderLeftColor: theme.colors.border, borderLeftWidth: 1, paddingLeft: theme.spacing.xs },
  marketMetricValue: StyleSheet.flatten([theme.textVariants.h3, { color: theme.colors.press, fontSize: 21, lineHeight: 26 }]),
  marketMetricsRow: { flexDirection: 'row', gap: theme.spacing.xs, marginTop: theme.spacing.s },
  marketTeamColumn: { alignItems: 'center', flex: 1, minWidth: 0 },
  marketTeamColumnRight: { borderLeftColor: theme.colors.border, borderLeftWidth: 1, paddingLeft: theme.spacing.xs },
  marketTeamTag: { color: theme.colors.paper, fontSize: 9, fontWeight: '800', letterSpacing: 0.6, overflow: 'hidden', paddingHorizontal: theme.spacing.s, paddingVertical: 3 },
  marketTeamsRow: { flexDirection: 'row', marginTop: theme.spacing.xs },
  noInjuries: StyleSheet.flatten([theme.textVariants.bodySmall, { color: theme.colors.textSecondary, marginTop: theme.spacing.xs }]),
  panel: {
    backgroundColor: colors.ink.paperWarm,
    borderColor: theme.colors.border,
    borderWidth: 1,
    elevation: 0,
    marginTop: 0,
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  panelCollapsed: {
    backgroundColor: colors.ink.paperWarm,
  },
  storylineText: StyleSheet.flatten([theme.textVariants.bodySmall, { marginTop: theme.spacing.xs }]),
  teamStatsAbbr: StyleSheet.flatten([theme.textVariants.caption, { flex: 1, fontSize: 9, fontWeight: '800', textAlign: 'center' }]),
  teamStatsColumns: { flexDirection: 'row', marginLeft: 'auto', width: '57%' },
  teamStatsHeading: { alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between', marginBottom: theme.spacing.xs },
  teamStatsLabel: StyleSheet.flatten([theme.textVariants.caption, { color: theme.colors.textSecondary, flex: 1.5, fontSize: 9, textAlign: 'center' }]),
  teamStatsRow: { alignItems: 'center', borderTopColor: theme.colors.border, borderTopWidth: 1, flexDirection: 'row', paddingVertical: theme.spacing.xs },
  teamStatsValue: StyleSheet.flatten([theme.textVariants.button, { flex: 1, fontSize: 11 }]),
  teamStatsValueRight: { textAlign: 'right' },
  teamTag: { color: theme.colors.paper, fontSize: 10, fontWeight: '800', paddingHorizontal: theme.spacing.xs, paddingVertical: 3 },
  teamTags: { flexDirection: 'row', gap: theme.spacing.xs },
  title: StyleSheet.flatten([theme.textVariants.button, { color: theme.colors.press, fontSize: 16 }]),
  titleRow: { alignItems: 'baseline', flexDirection: 'row', gap: theme.spacing.xs },
  toggle: { alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between', padding: theme.spacing.s },
  toggleCollapsed: {
    alignSelf: 'stretch',
    backgroundColor: colors.ink.paperWarm,
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.s,
    paddingVertical: theme.spacing.xs,
  },
  unavailable: StyleSheet.flatten([theme.textVariants.bodySmall, { color: theme.colors.textSecondary, marginTop: theme.spacing.m }]),
}));
